import { useCallback, useEffect, useMemo, useState } from "react";
import { listFields, updateField } from "../../../api/fieldsApi";
import { uploadMedia } from "../../../api/mediaApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import DashboardPanel from "../../../components/dashboard/DashboardPanel";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import { groupSectionFields } from "../../../utils/groupSectionFields";
import FieldInputRenderer from "./FieldInputRenderer";

function FieldCard({ field, value, onChange, onUpload, uploadBusy }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/50 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {field.fieldType || "text"}
        </span>
        <code className="rounded-md bg-white px-2 py-0.5 text-[11px] text-slate-500 ring-1 ring-slate-200">
          {field.key}
        </code>
      </div>
      <FieldInputRenderer
        field={field}
        value={value}
        onChange={onChange}
        onUpload={onUpload}
        uploadBusy={uploadBusy}
      />
    </div>
  );
}

export default function DynamicFieldsEditor({
  sectionId,
  excludeKeyPrefix = "",
}) {
  const notify = useNotify();
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadBusyId, setUploadBusyId] = useState(null);

  const visibleFields = useMemo(() => {
    const prefix = String(excludeKeyPrefix || "").toLowerCase();
    if (!prefix) return fields;
    return fields.filter(
      (field) => !String(field.key || "").toLowerCase().startsWith(prefix)
    );
  }, [fields, excludeKeyPrefix]);

  const fieldGroups = useMemo(
    () => groupSectionFields(visibleFields),
    [visibleFields]
  );

  const hasChanges = useMemo(
    () =>
      visibleFields.some((field) => {
        const fieldType = (field.fieldType || "").toLowerCase();
        if (fieldType === "image") return false;
        return (values[field.id] ?? "") !== (field.value ?? "");
      }),
    [visibleFields, values]
  );

  const loadFields = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listFields(sectionId);
      const list = Array.isArray(data) ? data : [];
      const sorted = list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setFields(sorted);
      const next = {};
      sorted.forEach((field) => {
        next[field.id] = field.value ?? "";
      });
      setValues(next);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  async function saveAllChanges() {
    const toSave = visibleFields.filter((field) => {
      const fieldType = (field.fieldType || "").toLowerCase();
      if (fieldType === "image") return false;
      return (values[field.id] ?? "") !== (field.value ?? "");
    });
    if (!toSave.length) return;

    setSaving(true);
    try {
      for (const field of toSave) {
        await updateField(field.id, {
          label: field.label,
          fieldType: field.fieldType,
          key: field.key,
          isRichText: field.isRichText,
          isRequired: field.isRequired,
          isPublished: field.isPublished,
          sortOrder: field.sortOrder,
          value: values[field.id] ?? "",
        });
      }
      notify.success("Changes saved");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleImageUpload(field, file) {
    setUploadBusyId(field.id);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mediaType", "image");
      form.append("groupKey", "fields");
      form.append("altText", file.name);
      form.append("isPrimary", "false");
      form.append("sortOrder", "0");

      const uploaded = await uploadMedia(sectionId, form);
      const url = uploaded?.url ?? uploaded?.Url ?? "";
      if (!url) throw new Error("Upload did not return a URL");

      setValues((prev) => ({ ...prev, [field.id]: url }));
      await updateField(field.id, {
        label: field.label,
        fieldType: field.fieldType,
        key: field.key,
        isRichText: field.isRichText,
        isRequired: field.isRequired,
        isPublished: field.isPublished,
        sortOrder: field.sortOrder,
        value: url,
      });
      notify.success("Image uploaded");
      await loadFields();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setUploadBusyId(null);
    }
  }

  if (loading) return <LoadingState message="Loading fields…" />;
  if (error) return <ErrorState message={error} onRetry={loadFields} />;
  if (!visibleFields.length) {
    return (
      <DashboardPanel title="No fields yet">
        <p className="text-sm text-slate-500">
          No content fields are configured for this section yet.
        </p>
      </DashboardPanel>
    );
  }

  return (
    <div className="space-y-5">
      {fieldGroups.map((group, groupIndex) => (
        <DashboardPanel
          key={group.id}
          title={group.title}
          description={group.description}
          badge={
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
              {String(groupIndex + 1).padStart(2, "0")}
            </span>
          }
        >
          <div className="grid gap-4 lg:grid-cols-2">
            {group.fields.map((field) => {
              const fieldType = (field.fieldType || "").toLowerCase();
              const isImageField = fieldType === "image";
              const isWide =
                fieldType === "image" ||
                field.isRichText ||
                (values[field.id] ?? "").length > 120;

              return (
                <div
                  key={field.id}
                  className={isWide ? "lg:col-span-2" : undefined}
                >
                  <FieldCard
                    field={field}
                    value={values[field.id]}
                    onChange={(next) =>
                      setValues((prev) => ({ ...prev, [field.id]: next }))
                    }
                    onUpload={
                      isImageField
                        ? (file) => handleImageUpload(field, file)
                        : undefined
                    }
                    uploadBusy={uploadBusyId === field.id}
                  />
                </div>
              );
            })}
          </div>
        </DashboardPanel>
      ))}

      <div className="sticky bottom-4 z-10 flex justify-end">
        <div className="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
          {hasChanges ? (
            <span className="text-xs font-medium text-amber-700">
              Unsaved changes
            </span>
          ) : (
            <span className="text-xs text-slate-400">All changes saved</span>
          )}
          <button
            type="button"
            onClick={saveAllChanges}
            disabled={saving || !hasChanges}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
